import { httpClient } from '../httpClient';
import {
  IProjectAsset,
  CreateProjectAssetRequest,
  UpdateProjectAssetRequest,
  PaginatedResponse,
  ProjectAssetQuery,
  PaginationQuery,
  GenerateAssetRequest,
  SystemAssetQuery
} from '@packages/global';

/**
 * 用户项目资源 API 模块
 * 处理用户项目资源管理操作
 */
export class ProjectAssetApi {
  /**
   * 获取项目资源分页列表（POST，强类型）
   */
  async projectAssetPageList(queryData: PaginationQuery<ProjectAssetQuery>): Promise<PaginatedResponse<IProjectAsset>> {
    return httpClient.post<PaginatedResponse<IProjectAsset>>('/user/projectAssets/page', queryData);
  }

  /**
   * 获取项目资源
   */
  async getProjectAssets(projectId: string): Promise<IProjectAsset[]> {
    return httpClient.get<IProjectAsset[]>(`/user/projectAssets/projects/${projectId}/assets`);
  }

  /**
   * 获取剧集资源
   */
  async getEpisodeAssets(episodeId: string): Promise<IProjectAsset[]> {
    return httpClient.get<IProjectAsset[]>(`/user/projectAssets/episodes/${episodeId}/assets`);
  }

  /**
   * 获取用户个人资源
   */
  async getUserAssets(): Promise<IProjectAsset[]> {
    return httpClient.get<IProjectAsset[]>('/user/projectAssets/user/assets');
  }

  /**
   * 获取系统级主体资源分页列表
   * @param queryData 分页查询参数
   */
  async systemAssetPageList(queryData: PaginationQuery<SystemAssetQuery>): Promise<PaginatedResponse<IProjectAsset>> {
    return httpClient.post<PaginatedResponse<IProjectAsset>>('/user/projectAssets/system/page', queryData);
  }

  /**
   * 获取系统级主体资源列表
   * @param query 查询参数（搜索、性别、国家、年龄区间等）
   * @deprecated 使用 systemAssetPageList 代替
   */
  async getSystemAssets(query?: SystemAssetQuery): Promise<IProjectAsset[]> {
    const params = new URLSearchParams();
    if (query?.search) params.append('search', query.search);
    if (query?.assetType) params.append('assetType', query.assetType);
    if (query?.gender) params.append('gender', query.gender);
    if (query?.country) params.append('country', query.country);
    if (query?.ageMin !== undefined) params.append('ageMin', query.ageMin.toString());
    if (query?.ageMax !== undefined) params.append('ageMax', query.ageMax.toString());
    const queryString = params.toString();
    return httpClient.get<IProjectAsset[]>(`/user/projectAssets/system/assets${queryString ? '?' + queryString : ''}`);
  }

  /**
   * 创建项目资源
   */
  async createProjectAsset(data: CreateProjectAssetRequest): Promise<IProjectAsset> {
    return httpClient.post<IProjectAsset>('/user/projectAssets', data);
  }

  /**
   * 更新项目资源
   */
  async updateProjectAsset(id: string, data: UpdateProjectAssetRequest): Promise<IProjectAsset> {
    return httpClient.put<IProjectAsset>(`/user/projectAssets/${id}`, data);
  }

  /**
   * 删除项目资源
   */
  async deleteProjectAsset(id: string): Promise<void> {
    return httpClient.delete<void>(`/user/projectAssets/${id}`);
  }

  /**
   * 收藏资源（复制为个人资源）
   */
  async favoriteAsset(id: string): Promise<IProjectAsset> {
    return httpClient.post<IProjectAsset>(`/user/projectAssets/${id}/favorite`);
  }

  /**
   * 生成主体图片
   */
  async generateAsset(
    id: string,
    modelId: string,
    options?: {
      userPrompt?: string;
      artStyle?: string;
    }
  ): Promise<IProjectAsset> {
    return httpClient.post<IProjectAsset>(`/user/projectAssets/${id}/generate`, {
      assetId: id,
      modelId,
      userPrompt: options?.userPrompt,
      artStyle: options?.artStyle
    } as GenerateAssetRequest);
  }

  /**
   * 取消主体生成
   */
  async cancelGeneration(id: string): Promise<IProjectAsset> {
    return httpClient.post<IProjectAsset>(`/user/projectAssets/${id}/cancel-generation`);
  }

  /**
   * 获取生成中的主体列表
   */
  async getGeneratingAssets(): Promise<IProjectAsset[]> {
    return httpClient.get<IProjectAsset[]>('/user/projectAssets/generating');
  }
}

// 导出单例实例
export const projectAssetApi = new ProjectAssetApi();
